import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {NgForm} from "@angular/forms";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {BehaviorSubject, Subject} from "rxjs";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {FloatingButtonAction, TypeAction} from "@share/component/floating-button/floating-button";
import {ConfirmService} from "@share/component/confirmation/confirm.service";
import {ListView} from "@domain/common/list-view/list-view";
import {FraisMissionService} from "@services/admin/frais/frais-mission.service";
import {DateApplication, FraisMissionDetail, IndemniteDetail} from "@domain/admin/frais/indemnites/frais-mission-detail";
import {PourcentageDomaine} from "@domain/admin/frais/indemnites/indemnites-parametres";
import {FraisMissionRegion} from "@domain/od/engagements/indemnites/frais-mission-region";
import {FraisMissionDuplicationRequest} from "@domain/admin/frais/indemnites/frais-mission-duplication-request";
import {DateApplicationPopinComponent} from "./date-application-popin/date-application-popin.component";
import {DateApplicationListItemComponent} from "./date-application-list-item/date-application-list-item.component";
import {GeographieView} from "@domain/geographie/geographieView";
import {TypeGeographie} from "@domain/geographie/typeGeographie";
import {Result, TypeCodeErreur} from "@domain/common/http/result";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {Devise} from "@domain/settings/devise";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import { FraisMissionDuplicationPopinComponent, FraisMissionDuplicationPopinData, FraisMissionDuplicationPopinResult } from './frais-mission-duplication-popin/frais-mission-duplication-popin.component';
import { FraisMissionFormUtils } from '../frais-mission-form.utils';


/**
 * Composant de détail d'un frais de mission
 * Permet la création et la modification des frais de mission avec gestion des dates d'application
 */
@Component({
    host: {'data-test-id': 'indemnites-frais-missions-detail'},
    selector: 'indemnites-frais-missions-detail',
    templateUrl: './indemnites-frais-missions-detail.component.html',
    styleUrls: ['./indemnites-frais-missions-detail.component.scss']
})
export class IndemnitesFraisMissionsDetailComponent implements OnInit, OnDestroy {
    /** Référence au formulaire */
    @ViewChild('fraisMissionForm') fraisMissionForm: NgForm;
    /** Identifiant du taux */
    idTaux: number;
    /** Mode création */
    isCreation: boolean;
    /** Données du frais de mission */
    fraisMission: FraisMissionDetail;
    /** Liste des dates d'application */
    listeDatesApplication: DateApplication[] = [];
    /** ListView pour les dates d'application */
    listeViewDatesApplication: ListView<DateApplication, DateApplicationListItemComponent>;
    /** Liste des domaines (Déjeuner, Dîner, Nuitée) */
    listeDomainesIndemnite: PourcentageDomaine[] = [];
    /** Onglets */
    listeTabItems: PageHeaderItem[] = [];
    /** Onglet sélectionné */
    selectedItem: PageHeaderItem;
    /** Actions du floating button */
    listeActions: BehaviorSubject<FloatingButtonAction[]> = new BehaviorSubject<FloatingButtonAction[]>([]);
    /** Indique si on vient d'un taux spécial (création depuis un territoire existant) */
    isTauxSpecialFromTerritoire: boolean = false;
    /** IdTaux parent (si création taux spécial) */
    idTauxParent: number;
    /** Liste des devises */
    listeDevises: Array<Devise> = [];
    /** Autocomplete territoire */
    selectedGeographie: GeographieView;
    /** Subject pour la gestion de la destruction du composant */
    private destroy$ = new Subject<void>();
    /**Etat de chargement pendant la duplication*/
    loading: boolean = false;
    //Snapshot période min initiale
    private periodeMinInitiale: number = null;
    //Snapshot période max initiale
    private periodeMaxInitiale: number = null;

    /**
     * Constructeur
     * @param route Service de route activée
     * @param router Service de routage
     * @param dialog Service de dialogue Material
     * @param translateService Service de traduction
     * @param toastrService Service de notification
     * @param confirmService Service de confirmation
     * @param fraisMissionService Service des frais de mission
     * @param store Store NgRx
     */
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private confirmService: ConfirmService,
        private fraisMissionService: FraisMissionService,
        private store: Store<AppState>
    ) {}

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Charger les devises
        this.store.select(state => state.settings?.Global)
            .pipe(filterFirstNotNull(), takeUntil(this.destroy$))
            .subscribe((settings) => { this.listeDevises = settings.listeDevises; });

        //Récupération de l'identifiant depuis la route
        this.route.params.pipe(first()).subscribe(params => {
            this.idTaux = +params['idTaux'];
            this.isCreation = this.idTaux === 0;

            //Vérifier si c'est une création de taux spécial depuis un territoire
            this.route.queryParams.pipe(first()).subscribe(queryParams => {
                if (queryParams['fromTerritoire']) {
                    this.isTauxSpecialFromTerritoire = true;
                    this.idTauxParent = +queryParams['fromTerritoire'];
                }
                this.initComponent();
            });
        });
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Initialise le composant
     */
    private initComponent(): void {
        this.initOnglets();
        this.initActions();
        this.initListView();
        this.loadData();
    }

    /**
     * Initialise les onglets
     */
    private initOnglets(): void {
        this.listeTabItems = [{
            code: 'generalites',
            libelle: 'admin.frais.indemnites.fraisMissions.detail.onglets.generalites'
        }];
        this.selectedItem = this.listeTabItems[0];
    }

    /**
     * Initialise les actions du floating button
     */
    private initActions(): void {
        const actions: FloatingButtonAction[] = [];

        //Bouton en modification
        if (!this.isCreation) {
            //Bouton de suppression du groupe de frais mission
            actions.push({
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.onDelete()
            });

            //Bouton de duplication du frais mission
            actions.push({
                type: TypeAction.SECONDARY,
                icone: 'material-icons-outlined',
                iconeName: 'save_as',
                libelle: 'global.actions.dupliquer',
                doAction: () => this.onDuplicate()
            });

            //Bouton d'enregistrement de la période en modification
            actions.push({
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'admin.frais.indemnites.fraisMissions.detail.generalites.enregistrerPeriode',
                doAction: () => this.onSavePeriode(),
                isDisabled: () => !this.canSavePeriode()
            });
        }

        this.listeActions.next(actions);
    }

    /**
     * Initialise la ListView des dates d'application
     */
    private initListView(): void {
        //Initialisation de la ListView des dates d'application
        this.listeViewDatesApplication = new ListView<DateApplication, DateApplicationListItemComponent>({
            uri: `/controller/FraisMission/listeDatesApplication/${this.idTaux}`,
            component: DateApplicationListItemComponent,
            title: this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.datesApplication.title'),
            isFilter: false,
            isFilterVisible: false,
            isSearchBar: false,
            mapResult: (result: any) => this.mapHistoriqueResult(result),
            listeActions: [{
                icon: 'add',
                onPress: () => this.openDateApplicationPopin(),
                tooltip: this.translateService.instant('global.actions.ajouter')
            }],
            extraOptions: {
                onEdit: (item: DateApplication) => this.openDateApplicationPopin(item),
                onDelete: (item: DateApplication) => this.onDeleteDateApplication(item)
            }
        });
    }

    /**
     * Mappe le résultat de l'historique pour la ListView
     * @param result Résultat de l'appel API
     * @returns Données formatées pour la ListView
     */
    private mapHistoriqueResult(result: any): any {
        //Extraction de la liste des dates d'application
        const dataList = result?.data?.listeDatesApplication || [];

        //Vérification que les données sont bien un tableau
        const items = Array.isArray(dataList) ? dataList : [];

        //Conversion des données brutes en objets DateApplication
        const mappedItems = items.map((item: any) => new DateApplication(item));

        //Tri par date décroissante
        mappedItems.sort((a, b) => {
            const dateA = a.dateApplication ? new Date(a.dateApplication).getTime() : 0;
            const dateB = b.dateApplication ? new Date(b.dateApplication).getTime() : 0;
            return dateB - dateA;
        });

        //Stocker localement pour le pré-remplissage à la création
        this.listeDatesApplication = mappedItems;

        return {
            listeResultats: mappedItems,
            numPage: 0,
            nbObjetsParPage: mappedItems.length,
            nbObjetsTotal: mappedItems.length,
            nbObjetsDansPage: mappedItems.length,
            nbPagesTotal: 1
        };
    }

    /**
     * Charge les données
     */
    private loadData(): void {
        //Charger les domaines d'indemnités
        this.fraisMissionService.loadListePourcentagesDomaine().pipe(first()).subscribe(domaines => {
            this.listeDomainesIndemnite = domaines || [];
        });

        //Charger les données du frais de mission selon le mode (création ou modification)
        if (this.isCreation) {
            this.initCreation();
        } else {
            this.loadFraisMission();
        }
    }

    /**
     * Initialise les données pour une création
     */
    private initCreation(): void {
        if (this.isTauxSpecialFromTerritoire && this.idTauxParent) {
            //Création d'un taux spécial depuis un territoire existant
            this.fraisMissionService.loadFraisMission(this.idTauxParent).pipe(first()).subscribe(frais => {
                this.fraisMission = new FraisMissionDetail({
                    fraisMissionRegion: frais.fraisMissionRegion,
                    periodeMin: frais.periodeMin,
                    periodeMax: frais.periodeMax,
                    devise: frais.devise,
                    hasTauxSpecial: true
                });
                //Initialiser selectedGeographie pour l'autocomplete
                this.selectedGeographie = this.mapRegionToGeographie(frais.fraisMissionRegion);
            });

        } else {
            //Création standard
            this.fraisMission = new FraisMissionDetail({
                devise: 'EUR',
                periodeMin: 1
            });
        }
    }

    /**
     * Charge un frais de mission existant
     */
    private loadFraisMission(): void {
        this.fraisMissionService.loadFraisMission(this.idTaux).pipe(first()).subscribe(fraisMission => {
            this.fraisMission = fraisMission;
            //Initialiser selectedGeographie pour l'autocomplete
            this.selectedGeographie = this.mapRegionToGeographie(fraisMission.fraisMissionRegion);

            //Mémorisation de la période initiale pour gérer l'activation du bouton enregistrer
            this.initPeriodeInitiale();
        });
    }

    /**
     * Ouvre la popin de sélection de territoire
     */
    openTerritoirePopin(): void {
        FraisMissionFormUtils.openTerritoirePopin(
            this.dialog,
            this.translateService,
            this.destroy$,
            (geo) => {
                this.fraisMission.fraisMissionRegion = FraisMissionFormUtils.mapGeographieToRegion(geo);
                this.selectedGeographie = geo;
            }
        );
    }

    /**
     * Mappe un FraisMissionRegion vers GeographieView pour l'autocomplete
     * @param region Région à mapper
     * @returns GeographieView correspondant à la région
     */
    private mapRegionToGeographie(region: FraisMissionRegion): GeographieView {
        //Si aucune région n'est fournie, retourne null
        if (!region) {
            return null;
        }

        //Crée un objet GeographieView avec le libellé de la région
        const geo: GeographieView = {
            id: null,
            type: null,
            libelle: region.libelleTerritoire
        };

        //Détermine le type de géographie et l'ID correspondant en fonction des propriétés de la région
        if (region.idPays) {
            geo.id = region.idPays;
            geo.type = TypeGeographie.PAYS;
        } else if (region.idVille) {
            geo.id = region.idVille;
            geo.type = TypeGeographie.VILLE;
        } else if (region.idZone) {
            geo.id = region.idZone;
            geo.type = TypeGeographie.ZONE;
        } else if (region.idRegion) {
            geo.id = region.idRegion;
            geo.type = TypeGeographie.REGION;
        }

        return geo;
    }

    /**
     * Ouvre la popin d'ajout/modification d'une date d'application
     * @param dateApplication Date d'application à modifier
     */
    openDateApplicationPopin(dateApplication?: DateApplication): void {
        const isNew = !dateApplication;

        //Préparer les données pour la popin
        const data: any = {
            dateApplication: dateApplication || this.createNewDateApplication(),
            listeDomaines: this.listeDomainesIndemnite,
            devise: this.fraisMission.devise,
            listeDevises: this.listeDevises,
            isCreation: isNew,
            montantsPrecedents: !isNew ? null : this.getDerniersMontants(),
            montantTotalPrecedent: isNew ? this.getDernierMontantTotal() : null
        };

        //Ouvre la popin de date d'application avec les données préparées
        const dialogRef = this.dialog.open(DateApplicationPopinComponent, {
            width: '600px',
            data: data
        });

        //Traite le résultat de la popin après fermeture
        dialogRef.afterClosed().pipe(filter(result => !!result)).subscribe((result: any) => {
            //Vérifier si c'est une action de suppression depuis la popin
            if (result.action === 'delete') {
                this.onDeleteDateApplication(result.dateApplication);
                return;
            }

            //Conserver la devise choisie dans la popin
            if (result.devise) {
                this.fraisMission.devise = result.devise;
            }

            //Enrichir avec les infos communes du frais de mission
            result.devise = this.fraisMission.devise;
            result.periodeMin = this.fraisMission.periodeMin;
            result.periodeMax = this.fraisMission.periodeMax;
            result.libelleSpecial = this.fraisMission.libelleSpecial;

            //Sécurité : si la région n'est pas encore mappée mais que l'autocomplete a une valeur, on remappe
            if (!this.fraisMission.fraisMissionRegion && this.selectedGeographie) {
                this.fraisMission.fraisMissionRegion = this.mapGeographieToRegion(this.selectedGeographie);
            }
            result.fraisMissionRegion = this.fraisMission.fraisMissionRegion;

            //Sauvegarder le FraisMission
            this.fraisMissionService.saveFraisMission(result).pipe(first()).subscribe(response => {
                if (response.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));

                    if (this.isCreation) {
                        //En création : récupérer l'idTaux créé et recharger
                        const newIdTaux = response.data?.fraisMission?.idTaux;
                        if (newIdTaux) {
                            //Naviguer et forcer le rechargement
                            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                                this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions', newIdTaux]);
                            });
                        }
                    } else {
                        //En modification : recharger les dates
                        this.reloadDatesApplication();
                    }
                } else {
                    TypeCodeErreur.showError(response.codeErreur, this.translateService, this.toastrService);
                }
            });
        });
    }

    /**
     * Retourne les montants de la dernière date d'application
     * @returns Liste des indemnités de la dernière date d'application ou null si aucune
     */
    private getDerniersMontants(): IndemniteDetail[] {
        //Retourner les indemnités de la première date d'application (la plus récente) si elle existe
        if (this.listeDatesApplication?.length > 0) {
            return this.listeDatesApplication[0].listeFraisMissionIndemnite;
        }
        return null;
    }

    /**
     * Crée une nouvelle date d'application avec les informations communes du frais de mission
     * @returns Nouvelle instance de DateApplication initialisée avec les valeurs par défaut
     */
    private createNewDateApplication(): DateApplication {
        return new DateApplication({
            devise: this.fraisMission.devise,
            periodeMin: this.fraisMission.periodeMin,
            periodeMax: this.fraisMission.periodeMax,
            libelleSpecial: this.fraisMission.libelleSpecial,
            fraisMissionRegion: this.fraisMission.fraisMissionRegion,
            listeFraisMissionIndemnite: this.listeDomainesIndemnite.map(d => ({
                idDomaine: d.idDomaine,
                libelleDomaine: d.libelleDomaine,
                pourcentage: d.pourcentage,
                montant: 0
            }))
        });
    }

    /**
     * Supprime une date d'application
     */
    onDeleteDateApplication(dateApplication: DateApplication): void {
        //Bloquer la suppression front si la date est déjà utilisée
        if (dateApplication?.dateUtilisee) {
            TypeCodeErreur.showError(TypeCodeErreur.ERROR_ALREADY_USED, this.translateService, this.toastrService);
            return;
        }

        //Vérifier si c'est la dernière date d'application
        const isDerniereDateApplication = this.listeDatesApplication.length === 1;

        //Message différent si c'est la dernière date
        const messageConfirmation = isDerniereDateApplication
            ? this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.datesApplication.confirmationSuppDerniere')
            : this.translateService.instant('global.suppression.confirmation');

        //Demande confirmation avant suppression
        this.confirmService.showConfirm(messageConfirmation).pipe(
            filter(confirmed => confirmed),
            switchMap(() => this.fraisMissionService.deleteFraisMission(dateApplication.idTaux))
        ).subscribe({
            next: (response: Result) => {
                if (response?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('global.success.suppression'));

                    //Si on supprime l'id de la page courante
                    if (dateApplication.idTaux === this.idTaux) {
                        //Trouver le prochain idTaux dans la liste
                        const autresIds = this.listeDatesApplication
                            .filter(d => d.idTaux !== dateApplication.idTaux)
                            .map(d => d.idTaux);

                        if (autresIds.length > 0) {
                            //Recharger avec le prochain id
                            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                                this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions', autresIds[0]]);
                            });
                        } else {
                            //Plus de dates, retourner à la liste
                            this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions']);
                        }
                    } else {
                        //Sinon juste recharger la liste
                        this.reloadDatesApplication();
                    }
                } else {
                    TypeCodeErreur.showError(response?.codeErreur || TypeCodeErreur.ERROR_DELETE, this.translateService, this.toastrService);
                }
            },
            error: () => {
                this.toastrService.error(this.translateService.instant('global.errors.suppression'));
            }
        });
    }

    /**
     * Recharge la liste des dates d'application
     */
    private reloadDatesApplication(): void {
        //Appeler refresh() pour recharger la liste
        this.listeViewDatesApplication?.refresh?.();
    }

    /**
     * Supprime le frais de mission
     */
    onDelete(): void {
        //Bloquer la suppression du groupe si au moins une date est déjà utilisée
        const hasDateUtilisee = this.listeDatesApplication?.some(date => !!date.dateUtilisee);
        if (hasDateUtilisee) {
            TypeCodeErreur.showError(TypeCodeErreur.ERROR_ALREADY_USED, this.translateService, this.toastrService);
            return;
        }

        //Demande de confirmation avant suppression, puis supprime le groupe de frais de mission et redirige vers la liste
        this.confirmService.showConfirm(
            this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.generalites.confirmationSuppression')
        ).pipe(
            filter(confirmed => confirmed),
            switchMap(() => this.fraisMissionService.deleteGroupeFraisMission(this.idTaux))
        ).subscribe({
            next: (response: Result) => {
                if (response?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    this.toastrService.success(this.translateService.instant('global.success.suppression'));
                    this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions']);
                } else {
                    TypeCodeErreur.showError(response?.codeErreur || TypeCodeErreur.ERROR_DELETE, this.translateService, this.toastrService);
                }
            },
            error: () => {
                this.toastrService.error(this.translateService.instant('global.errors.suppression'));
            }
        });
    }

    /**
     * Retour à la liste
     */
    onBack(): void {
        this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions']);
    }

    /**
     * Gestion du changement de territoire depuis l'autocomplete
     */
    onTerritoireChange(geographie: GeographieView): void {
        //Si une géographie est sélectionnée, on mappe la région
        if (geographie) {
            this.fraisMission.fraisMissionRegion = this.mapGeographieToRegion(geographie);
            this.selectedGeographie = geographie;
        } else {
            //Si la sélection est vidée, on nettoie aussi la région métier
            this.selectedGeographie = null;
            this.fraisMission.fraisMissionRegion = null;
        }
    }

    /**
     * Mappe un GeographieView vers FraisMissionRegion
     * @param geo GeographieView à mapper
     * @returns FraisMissionRegion correspondant à la géographie
     */
    private mapGeographieToRegion(geo: GeographieView): FraisMissionRegion {
        const region: FraisMissionRegion = { idTaux: 0 };
        region.libelleTerritoire = geo.libelle;

        //Récupérer le codeIso depuis le pays (si disponible)
        if (geo.pays?.code2) {
            region.codeIso = geo.pays.code2;
        }

        switch (geo.type) {
            case TypeGeographie.PAYS:
                region.idPays = geo.id;
                break;
            case TypeGeographie.VILLE:
                region.idVille = geo.id;
                break;
            case TypeGeographie.ZONE:
                region.idZone = geo.id;
                break;
            case TypeGeographie.REGION: // Territoire
                region.idRegion = geo.id;
                break;
        }
        return region;
    }

    /**
     * Retourne le montant total de la dernière date d'application
     * @returns Montant total de la dernière date d'application ou null si aucune
     */
    private getDernierMontantTotal(): number {
        if (this.listeDatesApplication?.length > 0) {
            return this.listeDatesApplication[0].montant;
        }
        return null;
    }

    /**
     * Ouvre la popin de duplication du frais de mission
     * Permet de sélectionner un nouveau territoire et de modifier les paramètres avant duplication
     */
    public onDuplicate(): void {
        //Ouvre la popin de duplication avec les données actuelles du frais de mission
        const dialogRef = this.dialog.open(FraisMissionDuplicationPopinComponent, {
            width: '80%',
            data: {
                fraisMission: this.fraisMission,
                selectedGeographie: this.selectedGeographie,
                isTauxSpecialFromTerritoire: this.isTauxSpecialFromTerritoire
            } as FraisMissionDuplicationPopinData
        });

        //Traite le résultat de la duplication si l'utilisateur a confirmé
        dialogRef.afterClosed()
            .pipe(filter(result => !!result))
            .subscribe((result: FraisMissionDuplicationPopinResult) => {
                this.onDuplicateConfirmed(result);
            });
    }

    /**
     * Traite la confirmation de duplication d'un frais de mission
     * Construit la requête de duplication et appelle le service backend
     * Redirige vers le nouveau frais de mission créé en cas de succès
     * @param result Résultat de la popin contenant le nouveau territoire et les paramètres modifiés
     */
    private onDuplicateConfirmed(result: FraisMissionDuplicationPopinResult): void {
        //Protection
        if (!result || !this.idTaux || !result.fraisMission || !result.selectedGeographie) {
            return;
        }

        //Mapping du territoire popin vers le modèle backend
        const fraisMissionRegion = this.mapGeographieToRegion(result.selectedGeographie);

        //Gestion cohérente du taux spécial
        const hasTauxSpecial = !!result.fraisMission.hasTauxSpecial;
        const libelleSpecial = hasTauxSpecial ? result.fraisMission.libelleSpecial : null;

        const payload: FraisMissionDuplicationRequest = {
            idTauxSource: this.idTaux,
            fraisMissionRegion: fraisMissionRegion,
            periodeMin: result.fraisMission.periodeMin,
            periodeMax: result.fraisMission.periodeMax,
            hasTauxSpecial: hasTauxSpecial,
            libelleSpecial: libelleSpecial
        };

        //Chargement pendant duplication
        this.loading = true;
        //Appel du service de duplication et gestion de la réponse
        this.fraisMissionService.duplicateGroupeFraisMission(payload).pipe(first()).subscribe({
            next: (response) => {
                this.loading = false;

                //Vérifie si la duplication a réussi
                if (response?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    const nouvelIdTaux = response.data?.fraisMission?.idTaux;
                    if (nouvelIdTaux) {
                        //Affiche un message de succès et redirige vers le nouveau frais de mission
                        this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                            this.router.navigate(['/Admin/Frais/Indemnites/FraisMissions', nouvelIdTaux]);
                        });
                        return;
                    }
                }

                //Affiche une erreur si la duplication a échoué
                TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService);
            },
            error: () => {
                //Gestion des erreurs techniques
                this.loading = false;
                TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService);
            }
        });
    }

    /**
     * Gère le changement de la case à cocher "Taux spécial"
     * Vide le libellé spécial si la case est décochée
     */
    onHasTauxSpecialChange(): void {
        this.fraisMission.libelleSpecial = FraisMissionFormUtils.getLibelleSpecialOnChange(
            this.fraisMission.hasTauxSpecial,
            this.fraisMission.libelleSpecial
        );
    }

    /**
     * Normalise periodeMax : 0 est traité comme null (illimité)
     * @param periodeMax Valeur de période max à normaliser
     * @returns La valeur normalisée (null si 0 ou null, sinon la valeur d'origine)
     */
    private normalizePeriodeMax(periodeMax: number): number {
        return (periodeMax == null || periodeMax === 0) ? null : periodeMax;
    }

    /**
     * Initialise le snapshot de période en mode modification
     */
    private initPeriodeInitiale(): void {
        if (!this.fraisMission) {
            return;
        }
        this.periodeMinInitiale = this.fraisMission.periodeMin ?? null;
        this.periodeMaxInitiale = this.normalizePeriodeMax(this.fraisMission.periodeMax);
    }

    /**
     * Gestion des changements de période dans le formulaire
     */
    onPeriodeChange(): void {
        if (!this.fraisMission) {
            return;
        }
        //Normalisation immédiate de la valeur 0 en null
        this.fraisMission.periodeMax = this.normalizePeriodeMax(this.fraisMission.periodeMax);
    }

    /**
     * Vérifie si la période a été modifiée par rapport à l'état initial
     * @returns true si periodeMin ou periodeMax ont changé depuis le chargement
     */
    private isPeriodeModifiee(): boolean {
        if (!this.fraisMission) {
            return false;
        }

        //Vérifie si la période a été modifiée par rapport à l'état initial
        const periodeMinCourante = this.fraisMission.periodeMin ?? null;
        const periodeMaxCourante = this.normalizePeriodeMax(this.fraisMission.periodeMax);

        return periodeMinCourante !== this.periodeMinInitiale
            || periodeMaxCourante !== this.periodeMaxInitiale;
    }

    /**
     * Vérifie la validité métier de la période
     * @returns true si la période est valide (periodeMin >= 1 et periodeMax >= periodeMin si défini)
     */
    private isPeriodeValide(): boolean {
        if (!this.fraisMission) {
            return false;
        }
        return FraisMissionFormUtils.isPeriodeValide(
            this.fraisMission.periodeMin,
            this.normalizePeriodeMax(this.fraisMission.periodeMax)
        );
    }

    /**
     * Détermine si le bouton enregistrer période doit être actif
     * @returns true si la période est valide et a été modifiée, false sinon
     */
    private canSavePeriode(): boolean {
        if (this.isCreation || !this.fraisMission) {
            return false;
        }
        return this.isPeriodeValide() && this.isPeriodeModifiee();
    }

    /**
     * Action d'enregistrement de la période
     */
    onSavePeriode(): void {
        if (!this.canSavePeriode()) {
            return;
        }

        //Normalisation de periodeMax : 0 => null (illimité)
        const periodeMaxNormalisee = this.normalizePeriodeMax(this.fraisMission.periodeMax);


        this.confirmService.showConfirm(this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.generalites.confirmationModificationPeriodeValidite')
        ).pipe(
            filter(confirmed => confirmed),
            switchMap(() => this.fraisMissionService.updatePeriodeGroupeFraisMission({
                idTaux: this.idTaux,
                periodeMin: this.fraisMission.periodeMin,
                periodeMax: periodeMaxNormalisee
            }))
        ).subscribe({
            next: (response: Result) => {
                //Succès
                if (response?.codeErreur === TypeCodeErreur.NO_ERROR) {
                    //Mise à jour locale + reset snapshot
                    this.fraisMission.periodeMax = periodeMaxNormalisee;
                    this.initPeriodeInitiale();

                    //Rafraîchissement des dates liées
                    this.reloadDatesApplication();

                    this.toastrService.success(this.translateService.instant('global.success.enregistrement'));
                    return;
                }

                //Erreur métier : période déjà utilisée
                if (response?.codeErreur === TypeCodeErreur.ERROR_ALREADY_USED) {
                    this.toastrService.error(
                        this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.generalites.erreurPeriodeExistante')
                    );
                    return;
                }

                //Autres erreurs
                TypeCodeErreur.showError(response?.codeErreur || TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService);
            },
            error: () => {
                //Erreur technique
                TypeCodeErreur.showError(TypeCodeErreur.ERROR_SAVE, this.translateService, this.toastrService);
            }
        });
    }
}